import { useState } from 'react';
import { Globe, ChevronDown } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useLanguage } from '@/i18n/LanguageContext';
import { lc } from '@/i18n/copy';

const TRANSLATION_LANGUAGES = [
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Español' },
  { value: 'fr', label: 'Français' },
  { value: 'pt', label: 'Português' },
  { value: 'it', label: 'Italiano' },
  { value: 'de', label: 'Deutsch' },
  { value: 'hi', label: 'Hindi' },
] as const;

export type TranslationLanguage = typeof TRANSLATION_LANGUAGES[number]['value'];

const copy = {
  en: { title: 'Message Language', writingIn: 'Writing in', recipientLang: 'Recipient language', sameNote: 'Same language — no translation needed' },
  es: { title: 'Idioma del Mensaje', writingIn: 'Escribiendo en', recipientLang: 'Idioma del destinatario', sameNote: 'Mismo idioma — no se necesita traducción' },
  fr: { title: 'Langue du Message', writingIn: 'Écriture en', recipientLang: 'Langue du destinataire', sameNote: 'Même langue — pas de traduction nécessaire' },
  pt: { title: 'Idioma da Mensagem', writingIn: 'Escrevendo em', recipientLang: 'Idioma do destinatário', sameNote: 'Mesmo idioma — sem tradução necessária' },
  ar: { title: 'لغة الرسالة', writingIn: 'الكتابة بـ', recipientLang: 'لغة المستلم', sameNote: 'نفس اللغة — لا حاجة للترجمة' },
  ja: { title: 'メッセージの言語', writingIn: '書いている言語', recipientLang: '受信者の言語', sameNote: '同じ言語 — 翻訳不要' },
  ko: { title: '메시지 언어', writingIn: '작성 언어', recipientLang: '수신자 언어', sameNote: '같은 언어 — 번역 불필요' },
  id: { title: 'Bahasa Pesan', writingIn: 'Menulis dalam', recipientLang: 'Bahasa penerima', sameNote: 'Bahasa sama — tidak perlu terjemahan' },
  hi: { title: 'संदेश की भाषा', writingIn: 'लिख रहे हैं', recipientLang: 'प्राप्तकर्ता की भाषा', sameNote: 'समान भाषा — अनुवाद की आवश्यकता नहीं' },
};

interface MessageLanguageSelectorProps {
  writingLanguage: TranslationLanguage;
  recipientLanguage: TranslationLanguage;
  onWritingLanguageChange: (lang: TranslationLanguage) => void;
  onRecipientLanguageChange: (lang: TranslationLanguage) => void;
}

export function MessageLanguageSelector({
  writingLanguage,
  recipientLanguage,
  onWritingLanguageChange,
  onRecipientLanguageChange,
}: MessageLanguageSelectorProps) {
  const { language } = useLanguage();
  const c = lc(copy, language);
  const areSame = writingLanguage === recipientLanguage;

  return (
    <div className="bg-muted/30 rounded-lg p-4 border border-border/50 space-y-3">
      <div className="flex items-center gap-2 text-sm font-medium text-foreground">
        <Globe className="h-4 w-4 text-primary" />
        {c.title}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">{c.writingIn}</label>
          <Select value={writingLanguage} onValueChange={(v) => onWritingLanguageChange(v as TranslationLanguage)}>
            <SelectTrigger className="h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TRANSLATION_LANGUAGES.map(l => (
                <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">{c.recipientLang}</label>
          <Select value={recipientLanguage} onValueChange={(v) => onRecipientLanguageChange(v as TranslationLanguage)}>
            <SelectTrigger className="h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TRANSLATION_LANGUAGES.map(l => (
                <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {areSame && (
        <p className="text-xs text-muted-foreground italic">{c.sameNote}</p>
      )}
    </div>
  );
}
